// packages/core/src/learning/schools/macro-money.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "Macro money". Matches boglehead-indexing.ts gold
// standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — held [] for the post-authoring cross-ref
//                     pass. Worldview/exhibit names: ray-dalio. (Keynes,
//                     Akerlof, Shiller, and Ferguson appear in canon as authors
//                     only — NOT figures-tally entries, and none currently
//                     exist as FigureIds.) ray-dalio is this school's lone
//                     figure and its SchoolMeta thinker — likely primary home.
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const MACRO_MONEY_EXHIBIT: SchoolExhibitEntry = {
	id: 'macro-money',
	tagline: 'Macro money believes the economy shows up in your personal life.',
	worldviewSummary:
		'Macro money is paying attention to the big forces that change everyday choices: inflation, interest rates, jobs, wages, housing, debt, currency, and public policy. This lens protects you from thinking your money life is only about personal discipline. It accepts that households live inside a wider economy they do not control. The blind spot is turning macro awareness into prediction addiction, doom thinking, or excuses. Macro money is strongest when it helps users prepare for changing conditions without pretending they can forecast everything.',
	coreBeliefs: [
		'Macro money believes personal finance happens inside a larger economy.',
		'Macro money believes inflation, rates, wages, housing, and jobs change what choices are realistic.',
		'Macro money believes a good plan should survive more than one economic environment.',
		'Macro money believes people should understand the forces around them without trying to predict every move.',
		'Macro money believes macro shocks hit households unevenly depending on income, debt, location, and runway.',
		'Macro money believes fear is not a plan, but neither is ignoring the economy.',
	],
	warnsAgainst: [
		'Treating personal finance like it happens outside inflation, rates, wages, and housing costs.',
		'Building a plan that only works when the economy is calm.',
		'Using macro headlines as an excuse to panic, time markets, or stop making practical moves.',
		'Blaming every problem on personal choices while ignoring the wider conditions around those choices.',
	],
	helps: 'This lens helps people understand why their rent, job security, debt costs, savings power, and freedom path can change when the wider economy changes. It may not fit when someone wants a prediction machine, because macro money is better for preparation than forecasting.',
	figures: [],
	canon: [
		{
			title: 'Principles for Dealing with the Changing World Order',
			author: 'Ray Dalio',
			oneLineIdea:
				'Currencies, debt, empires, productivity, and power shift over time, and those shifts can affect normal households.',
		},
		{
			title: 'The General Theory of Employment, Interest and Money',
			author: 'John Maynard Keynes',
			oneLineIdea:
				'Weak demand, jobs, rates, and spending can shape what happens to workers and businesses.',
		},
		{
			title: 'Animal Spirits',
			author: 'George Akerlof and Robert Shiller',
			oneLineIdea:
				'Confidence, fear, stories, and expectations can move economies, not just clean formulas.',
		},
		{
			title: 'The Ascent of Money',
			author: 'Niall Ferguson',
			oneLineIdea:
				'Finance, credit, money, and markets have shaped ordinary life across history.',
		},
		{
			title: 'Cost of living lens',
			oneLineIdea:
				'A money plan should be tested against the prices, rates, and wages people actually face.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-macro-money',
	debateIds: [],
	applyActions: [
		{
			label: 'See how macro pressure shows up in your current picture',
			action: {
				type: 'openDashboard',
				label: 'Review your current picture',
			},
		},
		{
			label: 'Test how higher prices change your freedom path',
			action: {
				type: 'openSimulator',
				label: 'Simulate higher costs',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Check how long your runway lasts if income gets shaky',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Write the macro risk your plan depends on most',
			action: {
				type: 'noToolAction',
				label: 'Name the macro risk',
				prompt: 'Write one economic change that could affect your plan, then write one buffer or choice that would make it less damaging.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Your budget does not live outside the economy.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of macro stress is the choice you thought you still had.',
			kind: 'hook',
		},
		{
			line: 'When did inflation, rent, rates, or layoffs change a plan you thought was solid?',
			kind: 'interview-question',
		},
		{
			line: 'What economic force shaped your money life more than personal discipline did?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps people connect big economic forces to their own numbers without turning them into fear.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Macro money teaches adults how the wider economy changes everyday freedom.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show how the same income feels different when rent, rates, food, and insurance move.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask someone what macro headline finally became personal for them.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
